/**
 * Time Machine #114 Phase 6 — pure request-body builder for the Lambda's
 * `getBlackoutDatesAuditByKellerId` method (introduced in queries-Lambda
 * `v05-08-2026-06-QRY01`).
 *
 * Drives the JobsDetailModal History section's "Customer Web Portal queried
 * availability" rows: every BlackoutDatesAudit invocation (#112) that ran
 * for the same kellerId as the currently-open job. Rows are merged into the
 * existing EventLog history feed and sorted by timestamp descending.
 *
 * Inputs:
 *   kellerId          — required string (matches inputs.kellerId in the
 *                       BlackoutDatesAudit collection; format: "ID-XXXXXXX").
 *   database          — "Warehouses" or "UAT". NOTE (#121): Lambda
 *                       `v05-09-2026-04-QRY01`+ overrides to "Warehouses"
 *                       for all Time Machine read methods regardless of
 *                       value passed.
 *   since (optional)  — Date or ISO string lower bound (inclusive) on invokedAt
 *   until (optional)  — Date or ISO string upper bound (inclusive) on invokedAt
 *   limit (optional)  — int, default 200, max 1000
 */

const VALID_DATABASES = ["Warehouses", "UAT"];
// kellerId in PROD looks like "ID-2295011" — leading "ID-" then 4-10 digits
// (length range observed in PROD samples; not strictly bounded). Use a
// conservative regex that catches the common shape but allows future growth.
const KELLER_ID_RE = /^ID-\d{1,15}$/;

export function buildBlackoutDatesAuditByKellerIdRequestBody({
  kellerId,
  database,
  since,
  until,
  limit,
} = {}) {
  if (!VALID_DATABASES.includes(database)) {
    throw new Error(
      `buildBlackoutDatesAuditByKellerIdRequestBody: database must be one of ${VALID_DATABASES.join(", ")} (got ${JSON.stringify(database)})`
    );
  }
  if (typeof kellerId !== "string" || kellerId.trim() === "") {
    throw new Error(
      `buildBlackoutDatesAuditByKellerIdRequestBody: kellerId is required (got ${JSON.stringify(kellerId)})`
    );
  }
  if (!KELLER_ID_RE.test(kellerId)) {
    throw new Error(
      `buildBlackoutDatesAuditByKellerIdRequestBody: kellerId must look like "ID-XXXXXXX" (digits after the "ID-" prefix; got ${JSON.stringify(kellerId)})`
    );
  }

  const body = {
    method: "getBlackoutDatesAuditByKellerId",
    database,
    // dispatcher artifact — the Lambda's createConnection requires `collection`
    // even though the handler picks BlackoutDatesAudit by name internally.
    collection: "BlackoutDatesAudit",
    kellerId,
  };

  if (since != null) {
    body.since = toIsoOrThrow(since, "since");
  }
  if (until != null) {
    body.until = toIsoOrThrow(until, "until");
  }
  if (limit != null) {
    if (typeof limit !== "number" || !Number.isFinite(limit) || limit <= 0) {
      throw new Error(
        `buildBlackoutDatesAuditByKellerIdRequestBody: limit must be a positive finite number (got ${JSON.stringify(limit)})`
      );
    }
    body.limit = Math.min(Math.floor(limit), 1000);
  }

  return body;
}

function toIsoOrThrow(value, label) {
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) {
    throw new Error(
      `buildBlackoutDatesAuditByKellerIdRequestBody: ${label} must be a Date or ISO string (got ${JSON.stringify(value)})`
    );
  }
  return d.toISOString();
}

export const __testables__ = {
  VALID_DATABASES,
  KELLER_ID_RE,
};
